/**
 * WaitlistPage — Admin view of all waitlist signups across shows
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  getAdminWaitlist,
  archiveWaitlistEntry,
  unarchiveWaitlistEntry,
  type WaitlistEntry,
  type WaitlistResponse,
} from '../../../api/admin';
// SDA-244: Notify reuses the battle-tested SDA-205 endpoint client (sends the
// branded email first, only stamps notified_at when it actually sent).
import { notifyWaitlistEntry } from '../../../api/events';
import ConfirmModal from '../../../components/ui/ConfirmModal';

function timeAgo(dateStr: string): string {
  const hours = Math.round((Date.now() - new Date(dateStr).getTime()) / (1000 * 60 * 60));
  if (hours < 1) return 'just now';
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days === 1) return '1d ago';
  return `${days}d ago`;
}

function isToday(dateStr: string): boolean {
  const d = new Date(dateStr);
  const now = new Date();
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
}

function formatTourDates(ts: { start_date: string; end_date: string } | null): string {
  if (!ts) return '';
  const fmt = (d: string) => new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${fmt(ts.start_date)} – ${fmt(ts.end_date)}`;
}

const STATUS_STYLES: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-700',
  notified: 'bg-primary-100 text-primary-700',
  converted: 'bg-success-100 text-success-700',
  archived: 'bg-warm-100 text-warm-600',
};

/**
 * SDA-244: sortable column header matching THIS page's header style
 * (px-5 py-3 text-xs uppercase). The shared SortableHeader component hardcodes a
 * larger px-6 py-4 text-sm non-uppercase style that would clash with the three
 * remaining static headers in the same row and misalign with the px-5 body cells,
 * so per the ticket's guidance we mirror its behavior (arrows, aria-sort, keyboard)
 * in a tiny file-local wrapper rather than introduce a new shared component.
 */
function SortTh({
  label,
  field,
  sortBy,
  sortOrder,
  onSort,
}: {
  label: string;
  field: string;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  onSort: (field: string) => void;
}) {
  const isActive = sortBy === field;
  return (
    <th
      className="px-5 py-3 text-left text-xs font-semibold text-warm-600 uppercase cursor-pointer select-none hover:text-warm-900"
      tabIndex={0}
      role="columnheader"
      aria-sort={isActive ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
      onClick={() => onSort(field)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSort(field);
        }
      }}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {isActive ? (
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d={sortOrder === 'asc' ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'} />
          </svg>
        ) : (
          <svg className="w-3 h-3 text-warm-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
          </svg>
        )}
      </span>
    </th>
  );
}

// SDA-244: notify modal needs the entry (name/email/show/notified_at) for its
// message, so pendingAction is a discriminated union across the three actions.
type PendingAction =
  | { mode: 'archive' | 'unarchive'; id: string }
  | { mode: 'notify'; entry: WaitlistEntry };

export default function WaitlistPage() {
  const [data, setData] = useState<WaitlistResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  // SDA-244: sort state — default matches the backend's created_at desc base order.
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  // SDA-226/SDA-244: archive/restore/notify action state — pendingAction drives the ConfirmModal.
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  const viewingArchived = statusFilter === 'archived';

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await getAdminWaitlist({
        status: statusFilter || undefined,
        page,
        sort_by: sortBy,
        sort_order: sortOrder,
      });
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load waitlist');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, page, sortBy, sortOrder]);

  useEffect(() => { load(); }, [load]);

  const totalPages = data ? Math.ceil(data.pagination.total / data.pagination.per_page) : 1;

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setPage(1);
  };

  const confirmArchiveAction = async () => {
    if (!pendingAction || pendingAction.mode === 'notify') return;
    try {
      setActionBusy(true);
      setActionError(null);
      setActionSuccess(null);
      if (pendingAction.mode === 'archive') {
        await archiveWaitlistEntry(pendingAction.id);
      } else {
        await unarchiveWaitlistEntry(pendingAction.id);
      }
      setPendingAction(null);
      await load(); // row leaves the current view; header count updates
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setActionBusy(false);
    }
  };

  const confirmNotify = async () => {
    if (!pendingAction || pendingAction.mode !== 'notify') return;
    const entry = pendingAction.entry;
    try {
      setActionBusy(true);
      setActionError(null);
      setActionSuccess(null);
      const result = await notifyWaitlistEntry(entry.id);
      setPendingAction(null);
      if (result.email_sent) {
        setActionSuccess(`Notification email sent to ${result.email}.`);
      } else {
        // The API tells us the email did NOT go out — never imply the studio was
        // notified. Surface the reason so the admin can fix the email connection.
        const why =
          result.reason === 'no_email_account'
            ? 'no email account is connected'
            : result.reason === 'no_recipient'
            ? 'this entry has no email address'
            : 'the email provider rejected the send';
        setActionError(
          `Email couldn't be sent (${why}) — the studio was NOT notified. Check the email connection in Settings.`
        );
      }
      await load(); // Pending → Notified badge updates
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to notify entry');
    } finally {
      setActionBusy(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl sm:text-3xl font-display font-bold text-warm-900">Waitlist</h1>
            {data && data.summary.total > 0 && (
              <span className="px-2.5 py-0.5 rounded-full text-sm font-bold bg-primary-100 text-primary-700">
                {data.summary.total}
              </span>
            )}
          </div>
          <p className="text-warm-600 mt-1 hidden sm:block">
            Studios waiting for spots on sold-out tour dates.
          </p>
        </div>
        <Link to="/admin" className="text-sm text-primary-600 hover:text-primary-700 font-medium">
          Back to Dashboard
        </Link>
      </div>

      {/* Show summary pills */}
      {data && data.summary.by_show.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {data.summary.by_show
            .sort((a, b) => b.count - a.count)
            .map(({ show_name, count }) => (
              <span
                key={show_name}
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-warm-100 text-warm-700"
              >
                {show_name}
                <span className="px-1.5 py-0.5 rounded-full bg-warm-200 text-warm-800 font-bold">{count}</span>
              </span>
            ))}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="w-full sm:w-48">
            <label className="block text-sm font-medium text-warm-700 mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className="w-full px-3 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="notified">Notified</option>
              <option value="converted">Converted</option>
              <option value="archived">Archived</option>
            </select>
          </div>
          {statusFilter && (
            <div className="flex items-end">
              <button
                onClick={() => { setStatusFilter(''); setPage(1); }}
                className="px-4 py-2.5 text-sm text-warm-600 hover:text-warm-900 hover:bg-warm-100 rounded-lg transition-colors"
              >
                Clear
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Loading */}
      {loading && !data && (
        <div className="bg-white rounded-xl shadow-sm">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="p-5 border-b border-warm-100 animate-pulse">
              <div className="h-5 bg-warm-100 rounded w-1/3 mb-2" />
              <div className="h-4 bg-warm-100 rounded w-1/2" />
            </div>
          ))}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700">{error}</div>
      )}

      {/* Empty */}
      {data && data.data.length === 0 && (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <h3 className="text-lg font-semibold text-warm-900 mb-2">No waitlist entries</h3>
          <p className="text-warm-600">
            {statusFilter ? 'Try changing the filter.' : 'No one has joined a waitlist yet.'}
          </p>
        </div>
      )}

      {/* Desktop table */}
      {data && data.data.length > 0 && (
        <>
          <div className="hidden md:block bg-white rounded-xl shadow-sm overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-warm-200 bg-warm-50">
                  <SortTh label="Contact" field="contact" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                  <SortTh label="Show" field="show" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                  <SortTh label="City" field="city" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                  <SortTh label="Tour Dates" field="tour_dates" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                  <th className="px-5 py-3 text-left text-xs font-semibold text-warm-600 uppercase">Signed Up</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-warm-600 uppercase">Status</th>
                  <th className="px-5 py-3 text-right text-xs font-semibold text-warm-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-warm-100">
                {data.data.map((entry: WaitlistEntry) => (
                  <tr key={entry.id} className="hover:bg-warm-50 transition-colors">
                    <td className="px-5 py-3">
                      <p className="text-sm font-medium text-warm-900">{entry.name || '—'}</p>
                      <p className="text-xs text-warm-500">{entry.email}</p>
                      {entry.studio_name && (
                        <p className="text-xs text-warm-400">{entry.studio_name}</p>
                      )}
                    </td>
                    <td className="px-5 py-3 text-sm text-warm-700">{entry.show_name || '—'}</td>
                    <td className="px-5 py-3 text-sm text-warm-700">{entry.tour_stop_label || '—'}</td>
                    <td className="px-5 py-3 text-sm">
                      <Link to={`/admin/tour-stops/${entry.tour_stop_id}/edit`} className="text-primary-600 hover:text-primary-700 hover:underline">
                        {formatTourDates(entry.tour_stops)}
                      </Link>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-warm-500">{timeAgo(entry.created_at)}</span>
                        {isToday(entry.created_at) && (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-success-100 text-success-700 uppercase">New</span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[viewingArchived ? 'archived' : entry.status] || ''}`}>
                        {viewingArchived ? 'Archived' : entry.status.charAt(0).toUpperCase() + entry.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right">
                      {viewingArchived ? (
                        <button
                          onClick={() => setPendingAction({ mode: 'unarchive', id: entry.id })}
                          className="px-3 py-1.5 text-sm font-medium text-primary-700 border border-primary-200 rounded-lg hover:bg-primary-50 transition-colors"
                        >
                          Restore
                        </button>
                      ) : (
                        <div className="flex items-center justify-end gap-2">
                          {/* Notify hidden once converted — the studio already booked (mirrors TourStopFormPage). */}
                          {entry.status !== 'converted' && (
                            <button
                              onClick={() => setPendingAction({ mode: 'notify', entry })}
                              className="px-3 py-1.5 text-sm font-medium text-primary-700 border border-primary-200 rounded-lg hover:bg-primary-50 transition-colors"
                            >
                              {entry.notified_at ? 'Notify Again' : 'Notify'}
                            </button>
                          )}
                          <button
                            onClick={() => setPendingAction({ mode: 'archive', id: entry.id })}
                            className="px-3 py-1.5 text-sm font-medium text-warm-700 border border-warm-300 rounded-lg hover:bg-warm-100 transition-colors"
                          >
                            Archive
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {data.data.map((entry: WaitlistEntry) => (
              <div key={entry.id} className="bg-white rounded-xl border border-warm-200 p-4">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div>
                    <p className="text-sm font-medium text-warm-900">{entry.name || entry.email}</p>
                    {entry.name && <p className="text-xs text-warm-500">{entry.email}</p>}
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {isToday(entry.created_at) && (
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-success-100 text-success-700 uppercase">New</span>
                    )}
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[viewingArchived ? 'archived' : entry.status] || ''}`}>
                      {viewingArchived ? 'Archived' : entry.status.charAt(0).toUpperCase() + entry.status.slice(1)}
                    </span>
                  </div>
                </div>
                <p className="text-sm text-warm-700">{entry.show_name} — {entry.tour_stop_label}</p>
                <div className="flex items-center justify-between mt-1">
                  <Link to={`/admin/tour-stops/${entry.tour_stop_id}/edit`} className="text-xs text-primary-600 hover:underline">{formatTourDates(entry.tour_stops)}</Link>
                  <p className="text-xs text-warm-400">{timeAgo(entry.created_at)}</p>
                </div>
                <div className="mt-3 pt-3 border-t border-warm-100 flex justify-end gap-2">
                  {viewingArchived ? (
                    <button
                      onClick={() => setPendingAction({ mode: 'unarchive', id: entry.id })}
                      className="px-3 py-1.5 text-sm font-medium text-primary-700 border border-primary-200 rounded-lg hover:bg-primary-50 transition-colors"
                    >
                      Restore
                    </button>
                  ) : (
                    <>
                      {entry.status !== 'converted' && (
                        <button
                          onClick={() => setPendingAction({ mode: 'notify', entry })}
                          className="px-3 py-1.5 text-sm font-medium text-primary-700 border border-primary-200 rounded-lg hover:bg-primary-50 transition-colors"
                        >
                          {entry.notified_at ? 'Notify Again' : 'Notify'}
                        </button>
                      )}
                      <button
                        onClick={() => setPendingAction({ mode: 'archive', id: entry.id })}
                        className="px-3 py-1.5 text-sm font-medium text-warm-700 border border-warm-300 rounded-lg hover:bg-warm-100 transition-colors"
                      >
                        Archive
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-6">
              <p className="text-sm text-warm-600">
                Showing {((page - 1) * data.pagination.per_page) + 1} to {Math.min(page * data.pagination.per_page, data.pagination.total)} of {data.pagination.total}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="px-3 py-1.5 text-sm border border-warm-200 rounded-lg hover:bg-warm-50 disabled:opacity-50 transition-colors"
                >
                  Previous
                </button>
                <span className="text-sm text-warm-600">Page {page} of {totalPages}</span>
                <button
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages}
                  className="px-3 py-1.5 text-sm border border-warm-200 rounded-lg hover:bg-warm-50 disabled:opacity-50 transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Archive/restore confirmation (shared styled modal — never window.confirm) */}
      {pendingAction && pendingAction.mode !== 'notify' && (
        <ConfirmModal
          title={pendingAction.mode === 'archive' ? 'Archive waitlist entry?' : 'Restore waitlist entry?'}
          message={
            pendingAction.mode === 'archive'
              ? 'Archive this waitlist entry? It will be hidden from the active list but kept, and can be restored from the Archived filter.'
              : 'Restore this waitlist entry back to the active list?'
          }
          confirmLabel={actionBusy ? 'Working…' : pendingAction.mode === 'archive' ? 'Archive' : 'Restore'}
          confirmVariant="primary"
          onConfirm={confirmArchiveAction}
          onCancel={() => { if (!actionBusy) { setPendingAction(null); setActionError(null); } }}
        />
      )}

      {/* Notify confirmation — the action emails a real studio contact, so preview
          who/what is sent before firing (mirrors TourStopFormPage, SDA-205). */}
      {pendingAction && pendingAction.mode === 'notify' && (
        <ConfirmModal
          title={pendingAction.entry.notified_at ? 'Send notification again?' : 'Notify this studio?'}
          message={`This will email ${pendingAction.entry.name || pendingAction.entry.email} at ${pendingAction.entry.email} for ${pendingAction.entry.show_name || 'this tour stop'} that a spot may be available.`}
          confirmLabel={actionBusy ? 'Sending…' : 'Send Email'}
          confirmVariant="primary"
          onConfirm={confirmNotify}
          onCancel={() => { if (!actionBusy) { setPendingAction(null); setActionError(null); } }}
        />
      )}

      {actionError && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[70] px-4 py-3 bg-danger-50 border border-danger-200 rounded-lg text-danger-700 shadow-lg text-sm">
          {actionError}
        </div>
      )}

      {actionSuccess && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[70] px-4 py-3 bg-success-50 border border-success-200 rounded-lg text-success-700 shadow-lg text-sm">
          {actionSuccess}
        </div>
      )}
    </div>
  );
}
